import React, { useCallback, useEffect, useRef, useState, useMemo } from "react"
import { MFMap, MFMarker, MFMarkerCluster } from "react-map4d-map"

import { SearchPlant } from "./components/SearchPlant"
import { generatePlantInfoWindowHTML } from "./components/PlantDetail"
import AutoPaginationList from "./components/PaginationList"
import MapLoading from "./components/MapLoading"
import { solarPlants } from "./data/solarPlants"

export default function App({ config, options, dispatchToVue }) {
  const mapRef = useRef(null)
  const didInitRef = useRef(false)
  const useZoomIn = useRef(true)
  // const currentPlantRef = useRef({})
  const widthSibar = 342
  const [isMapLoading, setIsMapLoading] = useState(false)
  const [isIconSwitching, setIsIconSwitching] = useState(true)
  const [isAutoSwitching, setIsAutoSwitching] = useState(true)
  const [isContinue, setIsContinue] = useState(false)

  const [siteId, setSiteId] = useState(null)
  const [initialSiteId, setInitialSiteId] = useState(null)
  const lastAutoSiteIdRef = useRef(null)

  const arrayPlants = useMemo(() => {
    console.log("options", options)

    return [...(options || [])]
  }, [options])

  useEffect(() => {
    const pause = Boolean(config?.isPause)
    setIsContinue(!pause)

    if (!didInitRef.current) {
      setIsAutoSwitching(pause)
      didInitRef.current = true
    } else {
      setIsAutoSwitching(pause)
    }
  }, [config?.isPause])

  useEffect(() => {
    const sid = Number(config?.siteId)
    if (!Number.isFinite(sid) || sid <= 0) return
    setSiteId(sid)
  }, [config?.siteId])

  // useEffect(() => {
  //   if (config?.siteId) {
  //     setInitialSiteId(config?.siteId)
  //   }
  // }, [config?.siteId])

  const onLastAutoSiteIdChange = useCallback(
    (sid) => {
      lastAutoSiteIdRef.current = sid
      dispatchToVue?.("last-auto-site-id", { siteId: sid })
    },
    [dispatchToVue],
  )

  const setIsIconSwitchingAndAuto = (val) => {
    setIsIconSwitching(val)
    setIsAutoSwitching(val)
    emitIsIconSwitchingMap(val)
  }

  useEffect(() => {
    const isIconSwitchingMap = Boolean(config?.isIconSwitchingMap)

    setIsIconSwitchingAndAuto(isIconSwitchingMap)
    if (isIconSwitchingMap) {
      setIsContinue(isIconSwitchingMap)
      setSearchTerm("")
      emitSiteStatusFilter("all")
    }
  }, [config?.isIconSwitchingMap, config?.isAltTemplate])

  useEffect(() => {
    setIsMapLoading(true)
    const timeout = setTimeout(() => {
      setIsMapLoading(false)
    }, 300) // 300ms delay để tránh giật

    return () => clearTimeout(timeout)
  }, [])

  // console.log("config", config)
  // console.log("options", options)

  // Emit click
  const emitIsIconSwitchingMap = (isScroll) => {
    dispatchToVue?.("icon-switch", isScroll)
  }

  const emitMarkerClick = (plant, triggerBy) => {
    dispatchToVue?.("marker-click", {
      ...plant,
      triggerBy,
    })
  }

  const emitStopScrollString = (isScroll) => {
    dispatchToVue?.("stop-scrolling", isScroll)
  }

  const emitSiteStatusFilter = (filter) => {
    dispatchToVue?.("site-status-filter", filter)
  }

  // Controlled view
  const [mapCenter, setMapCenter] = useState({ lat: 10.769589, lng: 106.754497 })
  const [mapZoom, setMapZoom] = useState(10)

  // Selection state

  // Search state
  const [searchTerm, setSearchTerm] = useState("")
  // const filteredPlants = (arrayPlants || [])?.filter((plant) => plant?.siteName?.toLowerCase().includes(searchTerm.toLowerCase()))
  const filteredPlants = useMemo(() => {
    if (!searchTerm) return arrayPlants
    return arrayPlants.filter((plant) => plant?.siteName?.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [arrayPlants, searchTerm])

  // Toggles
  const [mapType, setMapType] = useState("roadmap")
  const [sidebarOpen, setSidebarOpen] = useState(true)

  // Cache map instance and set type
  const handleMapReady = (map) => {
    mapRef.current = map
    map.setMapType(mapType === "roadmap" ? window.map4d.MapType.roadmap : window.map4d.MapType.satellite)
  }

  // Toggle roadmap/satellite
  const toggleMapType = () => {
    const next = mapType === "roadmap" ? "satellite" : "roadmap"
    setMapType(next)
    mapRef.current?.setMapType(next === "roadmap" ? window.map4d.MapType.roadmap : window.map4d.MapType.satellite)
  }

  // Custom cluster icon
  const clusterIcon = useCallback(() => new window.map4d.Icon(50, 50, `${process.env.PUBLIC_URL}/icons/markerBlue.png`), [])

  const goToSite = (plant) => {
    if (!plant || !plant.latitude || !plant.longitude) {
      return
    }

    const zoomValue = useZoomIn.current ? 14 : 16

    setMapZoom(zoomValue)
    setMapCenter({ lat: plant.latitude, lng: plant.longitude })

    const zoomAnchor = useZoomIn.current
      ? mapRef.current?._data?._mapView?.zoomInButton?.querySelector(".mf-zoom-in-icon")
      : mapRef.current?._data?._mapView?.zoomOutButton?.querySelector(".mf-zoom-out-icon")

    if (zoomAnchor) {
      setTimeout(() => {
        const evt = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
        })
        zoomAnchor.dispatchEvent(evt)
      }, 50)
    }

    useZoomIn.current = !useZoomIn.current
  }

  // Marker click: open popup only
  const handleMarkerClick = (plant) => {
    // console.log("handleMarkerClick", plant)
    setIsIconSwitchingAndAuto(false)
    setSidebarOpen(false)

    // Emit to Vue
    emitMarkerClick(plant, "handleMarkerClick")
    emitStopScrollString(false)
  }

  // Sidebar click: recenter & zoom (no popup)
  const handleSidebarClick = (plant) => {
    // console.log("handleSidebarClick", plant)
    setIsIconSwitchingAndAuto(false)
    goToSite(plant)

    // Emit to Vue
    emitMarkerClick(plant, "handleSidebarClick")
    emitStopScrollString(false)
  }

  const hoverProps = {
    onMouseOver: sidebarOpen
      ? undefined
      : (e) => {
          e.currentTarget.style.background = "#f3f3f3"
          e.currentTarget.style.boxShadow = "0 2px 5px rgba(0,0,0,0.2)"
        },
    onMouseOut: (e) => {
      e.currentTarget.style.background = "#fff"
      e.currentTarget.style.boxShadow = "none"
    },
  }

  const markerElements = useMemo(() => {
    return (arrayPlants || []).map((plant) => (
      <MFMarker
        key={plant.siteId}
        position={{ lat: plant.latitude, lng: plant.longitude }}
        onClick={() => handleMarkerClick(plant)}
        style={{ color: "#fff" }}
        infoWindow={generatePlantInfoWindowHTML(plant)}
      />
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrayPlants])

  return (
    // <div style={{ width: "918px", height: "745px", display: "flex", margin: "100px auto", borderRadius: 24, overflow: "hidden" }}>
    <div
      style={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        margin: 0,
        padding: 0,
        position: "relative",
        fontFamily: "Inter, sans-serif",
        overflow: "hidden",
      }}>
      <style>
        {`
          .mf-info-window-container {
            border-radius: 12px !important;
          }
          .mf-info-window-content {
            color: #1F1F21 !important;
            border-radius: 12px !important;
          }
          .mf-scrollbar {
            overflow: visible !important;
          }
        `}
      </style>
      {/* Map */}
      {isMapLoading && <MapLoading />}
      <MFMap
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
        accessKey="67527ee336324c9dce43ea97db3c29c7"
        version="2.6.6"
        zoom={mapZoom}
        options={{ controls: true, center: mapCenter, zoom: mapZoom }}
        onMapReady={handleMapReady}>
        <MFMarkerCluster
          minZoom={0}
          maxZoom={14}
          radius={100}
          zoomOnClick
          clusterIcon={clusterIcon}>
          {markerElements}
        </MFMarkerCluster>
      </MFMap>

      {/* Sidebar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: sidebarOpen ? 0 : -widthSibar - 24,
          width: widthSibar,
          height: "100%",
          maxHeight: "713px",
          background: "#fff",
          overflowY: "hidden",
          borderRadius: 24,
          boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
          transition: "left 0.3s",
          zIndex: 1000,
          padding: "12px 12px 16px",
        }}>
        {/* Search bar */}
        <SearchPlant
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredPlants={filteredPlants}
          setIsIconSwitchingAndAuto={setIsIconSwitchingAndAuto}
          solarPlants={arrayPlants || []}
          handleSidebarClick={handleSidebarClick}
          setSiteId={setSiteId}
          emitStopScrollString={emitStopScrollString}
        />

        {/* Plant list */}
        <AutoPaginationList
          filteredPlants={filteredPlants}
          itemsPerPage={4}
          itemHeight={148}
          handleSidebarClick={handleSidebarClick}
          isAutoSwitching={isAutoSwitching}
          setIsIconSwitchingAndAuto={setIsIconSwitchingAndAuto}
          emitMarkerClick={emitMarkerClick}
          emitStopScrollString={emitStopScrollString}
          timeSwitching={config?.timeSwitching}
          isAltTemplate={config?.isAltTemplate}
          solarPlants={arrayPlants || []}
          initialSiteId={initialSiteId}
          siteId={siteId}
          isContinue={isContinue}
          setIsContinue={setIsContinue}
          setIsAutoSwitching={setIsAutoSwitching}
          searchTerm={searchTerm}
          onLastAutoSiteIdChange={onLastAutoSiteIdChange}
        />
      </div>

      {/* Sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        {...hoverProps}
        style={{
          position: "absolute",
          top: "50%",
          left: sidebarOpen ? widthSibar - 12 : 0,
          transform: "translateY(-50%)",
          width: 30,
          height: 30,
          borderRadius: "50%",
          border: "0px solid #ccc",
          background: "#fff",
          color: "#1F1F21",
          cursor: "pointer",
          zIndex: 1001,
          padding: "2px 0 0",
          transition: "all 0.3s",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
        {
          <img
            src={`${process.env.PUBLIC_URL}/icons/${sidebarOpen ? "arrow_left" : "arrow_right"}.svg`}
            alt={"arrow_left"}
            style={{
              width: 16,
              height: 16,
            }}
          />
        }
      </button>

      {/* Map type toggle */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}>
        <button
          key={`${isIconSwitching ? "pause" : "play"}`}
          onClick={() => {
            const newValue = !isIconSwitching

            if (newValue) {
              // 1) reset filter trước (Vue sẽ trả list all)
              emitSiteStatusFilter("all")
              setSearchTerm("")

              // 2) ép quay về site auto đã lưu
              const lastSidFromVue = Number(config?.lastAutoSiteId)
              const lastSid = Number.isFinite(lastSidFromVue) && lastSidFromVue > 0 ? lastSidFromVue : lastAutoSiteIdRef.current

              if (lastSid != null) {
                setSiteId(null)
                setTimeout(() => setSiteId(Number(lastSid)), 0)
              }

              // 3) bật auto sau cùng
              setIsContinue(true)
              setIsIconSwitchingAndAuto(true)
              emitStopScrollString(true)
            } else {
              setIsIconSwitchingAndAuto(false)
              emitStopScrollString(false)
            }
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.8)")}
          onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.6)")}
          style={{
            padding: "8px 12px",
            background: "rgba(255,255,255,0.6)",
            color: "#1F1F21",
            border: "1px solid #ccc",
            borderRadius: 5,
            cursor: "pointer",
          }}>
          <img
            key={`${isIconSwitching ? "pause" : "play"}`}
            src={`${process.env.PUBLIC_URL}/icons/${isIconSwitching ? "pause" : "play"}.svg`}
            alt={isIconSwitching ? "pause" : "play"}
            style={{
              width: 16,
              height: 16,
            }}
          />
        </button>
        <button
          onClick={toggleMapType}
          onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.8)")}
          onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.6)")}
          style={{
            padding: "4px 16px",
            background: "rgba(255,255,255,0.6)",
            color: "#1F1F21",
            border: "1px solid #ccc",
            borderRadius: 5,
            cursor: "pointer",
          }}>
          {mapType === "roadmap" ? "Satellite" : "Basic Map"}
        </button>
      </div>
    </div>
    // </div>
  )
}
